import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AxiosError, AxiosResponse } from 'axios';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import {
    entityConfig,
    entityConfigToType,
    MutableEntityType,
} from 'app/agriculture/mutations/types/entityConfig';
import { handleZodParseError } from 'app/agriculture/utils';
import { useEntityCacheInvalidation } from 'app/agriculture/mutations/useEntityCacheInvalidation';
import { convertDatePropertiesToTZDate } from 'app/agriculture/queries/utils/convertDatePropertiesToTZDate';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

/**
 * This mutation does not support entities with snapshots
 */
export function useEntityUpdateMutation<T extends MutableEntityType>(entityType: T) {
    const { timeZone } = useTimeZone();
    const { agricultureToastService } = useContext(ServicesContext);
    const instance = createAxiosInstance();
    const config = entityConfig[entityType];
    const { updateCache } = useEntityCacheInvalidation(entityType);

    type EntityType = entityConfigToType[T];
    type RequestType = Partial<EntityType> & Pick<EntityType, 'id'>;

    return useMutation<EntityType, AxiosError<EntityType, RequestType>, RequestType>({
        mutationFn: async function (payload: RequestType): Promise<EntityType> {
            //console.log(JSON.stringify(payload)); // this is really helpful for nock debugging
            const { data } = await instance.patch<
                EntityType,
                AxiosResponse<EntityType>,
                RequestType
            >(`${config.endpoint}${payload.id.toString()}/`, payload);
            try {
                return config.schema
                    .transform((data) => convertDatePropertiesToTZDate(data, timeZone))
                    .parse(data) as EntityType;
            } catch (e) {
                handleZodParseError(e);
                throw e;
            }
        },
        onSuccess: function (data: EntityType) {
            updateCache(data);
        },
        onError: async function (error) {
            console.error('Error updating entity', error);
            await agricultureToastService.showToast({
                title: `${entityType} Update Failed`,
                subtext: 'Something went wrong',
                type: AgricultureToastType.ERROR,
                variant: 'simple',
            });
        },
    });
}
